import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { Button, Dropdown, Icon, Label, Stack, TextField } from '@bingads-webui-react/fabric-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';

const defaultLabelStyle = options => ({
  label: {
    color: options.success ? '#0A610A' : '#A80000',
    backgroundColor: options.success ? '#DFF6DD' : '#FDE7E9',
    fontWeight: 600,
    fontSize: 12,
    border: 4,
    paddingInline: 5,
    paddingTop: 2,
    paddingBottom: 2,
    verticalAlign: 'middle',
    borderRadius: 4,
    marginLeft: 8,
  },
  labelIcon: {
    fontSize: 14,
    height: 14,
    width: 14,
    fontWeight: 400,
    marginLeft: 4,
    marginRight: 5,
    verticalAlign: '-2.5px',
  },
});

const defaultStyles = {
  disconnectedStatusTile: {
    root: {
      marginLeft: 22,
    },
  },
  connectedStatusTile: {
    root: {
      marginLeft: 22,
    },
  },
  connectedStatusLabel: {
    root: {
      color: '#0C5E0C',
      fontWeight: 600,
      fontSize: 12,
      lineHeight: 'normal',
    },
  },
  saveButton: {
    root: {
      width: 68,
    },
  },
  removeButton: {
    root: {
      width: 89,
    },
  },
};

const VerifiedTrackingSettingKey = 'VerifiedTrackingSettings';
const vendorNameKey = 'vendorName';
const advertiserEntityIdKey = 'advertiserEntityId';
const publisherEntityIdKey = 'publisherEntityId';
const regexMappping = {
  video: [/v2\/(\d*)\/(\d*)/],
  display: [/rjss\/st\/(\d*)\/(\d*)/, /rfw\/st\/(\d*)\/(\d*)/],
  native: [/rjss\/st\/(\d*)\/(\d*)/, /rfw\/st\/(\d*)\/(\d*)/],
};

const ThirdPartyMeasurementNameMap = {
  [advertiserEntityIdKey]: _TL_('Advertiser ID: '),
  [publisherEntityIdKey]: _TL_('Publisher ID: '),
  [vendorNameKey]: _TL_('Vendor Name: '),
};

export const formatThirdPartyMeasurementSetting = (verifiedTrackingSettings, i18n) => {
  const verifiedTrackingSetting = _.first(verifiedTrackingSettings);
  const vendorName = _.get(verifiedTrackingSetting, vendorNameKey, '');
  const advertiserEntityId = _.get(verifiedTrackingSetting, advertiserEntityIdKey, '');
  const publisherEntityId = _.get(verifiedTrackingSetting, publisherEntityIdKey, '');

  const settingEmpty = _.isEmpty(vendorName) || _.isEmpty(advertiserEntityId) || _.isEmpty(publisherEntityId);

  const getVerifiedTrackingComponent = (key, value) =>
    (<div>{i18n.getString(ThirdPartyMeasurementNameMap[key]) + value}</div>);

  return (
    <div white-space="pre">
      {
        settingEmpty ?
          i18n.getString(_TL_('None')) :
          (
            <div>
              {getVerifiedTrackingComponent(vendorNameKey, vendorName)}
              {getVerifiedTrackingComponent(publisherEntityIdKey, publisherEntityId)}
              {getVerifiedTrackingComponent(advertiserEntityIdKey, advertiserEntityId)}
            </div>
          )
      }
    </div>
  );
};

export const extractIdsFromIASInput = ({ value, adType }) => {
  const regexStrArr = regexMappping[adType];
  let result = [];
  _.each(regexStrArr, (regexStr) => {
    const regex = new RegExp(regexStr);
    const currentRes = regex.exec(value);
    if (currentRes && currentRes.length >= 3) {
      result = currentRes;
    }
  });

  if (result.length >= 3) {
    return [result[1], result[2]];
  }

  return [];
};

const errorMessages = {
  advertiserEntityId: 'advertiserEntityId',
  publisherEntityId: 'publisherEntityId',
};

const createVerifiedTrackingSettingViewModel = value => ({
  [VerifiedTrackingSettingKey]: _.isEmpty(value) ? [] : [value],
});

const StatusLabel = ({
  value, i18n, success, type,
}) => {
  let statusMessage = i18n.getString(_TL_('Unknown status'));
  if (type === advertiserEntityIdKey) {
    statusMessage = success ? i18n.getString(_TL_('IAS Advertiser ID: {0}')).replace('{0}', value) : i18n.getString(_TL_('IAS Advertiser ID'));
  } else if (type === publisherEntityIdKey) {
    statusMessage = success ? i18n.getString(_TL_('IAS Publisher ID: {0}')).replace('{0}', value) : i18n.getString(_TL_('IAS Publisher ID'));
  }

  const statusStyle = useMemo(() => defaultLabelStyle({ success }), [success]);

  return (
    <Label
      style={statusStyle.label}
      className={`status-label-${type}`}
    >
      <Icon
        iconName={success ? 'Accept' : 'Blocked2'}
        style={statusStyle.labelIcon}
      />
      {statusMessage}
    </Label>
  );
};

export const ThirdPartyMeasurement = (props) => {
  const {
    i18n,
    updateValidation,
    validationProps,
    adType = 'video',
    overrideStyles = {},
  } = props;

  const { data } = validationProps;
  const verifiedTrackingData = useMemo(
    () => _.result(data, [VerifiedTrackingSettingKey, 0], {})
    , [data]
  );
  const styles = useMemo(() => ({
    ...defaultStyles,
    ...overrideStyles,
  }), [overrideStyles]);

  const [connected, setConnected] = useState(false);
  const [verifiedTrackingSettingRaw, setVerifiedTrackingSettingRaw] = useState('');
  const [tagUrlErrors, setTagErrors] = useState([]);

  const options = useMemo(() => [
    { key: 'IAS', text: i18n.getString(_TL_('Integral Ad Science (IAS)')) },
  ], [i18n]);

  useEffect(() => {
    if (
      !!verifiedTrackingData &&
      !!verifiedTrackingData[vendorNameKey] &&
      !!verifiedTrackingData[advertiserEntityIdKey] &&
      !!verifiedTrackingData[publisherEntityIdKey]) {
      setConnected(true);
    } else {
      setConnected(false);
    }
  }, [verifiedTrackingData]);

  const validateUrl = useCallback((value) => {
    const ids = extractIdsFromIASInput({ value, adType });
    let result = true;
    const verifiedTrackingSettingToAdd = {};
    if (ids.length === 2) {
      const advertiserEntityId = ids[0];
      const publisherEntityId = ids[1];
      const errors = [];
      if (_.isEmpty(advertiserEntityId)) {
        errors.push(errorMessages.advertiserEntityId);
        result = false;
      } else {
        verifiedTrackingSettingToAdd[advertiserEntityIdKey] = advertiserEntityId;
      }

      if (_.isEmpty(publisherEntityId)) {
        errors.push(errorMessages.publisherEntityId);
        result = false;
      } else {
        verifiedTrackingSettingToAdd[publisherEntityIdKey] = publisherEntityId;
      }

      setTagErrors(errors);
    } else {
      setTagErrors([errorMessages.advertiserEntityId, errorMessages.publisherEntityId]);
      result = false;
    }

    return { result, verifiedTrackingSettingToAdd };
  }, [adType]);

  const save = useCallback(() => {
    const { result, verifiedTrackingSettingToAdd } = validateUrl(verifiedTrackingSettingRaw);
    if (result
    && verifiedTrackingSettingToAdd[advertiserEntityIdKey]
    && verifiedTrackingSettingToAdd[publisherEntityIdKey]) {
      const value = {
        [vendorNameKey]: options[0].key,
        [advertiserEntityIdKey]: verifiedTrackingSettingToAdd[advertiserEntityIdKey],
        [publisherEntityIdKey]: verifiedTrackingSettingToAdd[publisherEntityIdKey],
      };
      // We will just pass the value to the Validate Group.
      updateValidation([], createVerifiedTrackingSettingViewModel(value));
    }
  }, [
    options,
    validateUrl,
    verifiedTrackingSettingRaw,
    updateValidation,
  ]);

  const remove = () => {
    updateValidation([], createVerifiedTrackingSettingViewModel());
  };

  const onRenderLabelDropDown = () => (
    <Stack verticalAlign="center" horizontal>
      <Label>
        {i18n.getString(_TL_('Third-party partners'))}
      </Label>
      <HelpPopupInline topic="pop_BA_VideoAds_Partners" />
    </Stack>
  );

  const onRenderLabelTextField = () => (
    <Stack verticalAlign="center" horizontal>
      <Label>
        {i18n.getString(_TL_('Tag URL'))}
      </Label>
      <HelpPopupInline topic="pop_BA_VideoAds_TagURL" />
    </Stack>
  );

  const onTagUrlChange = useCallback((event, value) => {
    // remove the error message when the user is typing.
    setTagErrors([]);
    setVerifiedTrackingSettingRaw(value);
  }, []);

  const tagErrorMessage = useMemo(() => {
    if (!_.isEmpty(tagUrlErrors)) {
      return (
        <Stack className="tag-error-message" horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          {i18n.getString(_TL_('Cannot detect:'))}
          {
            _.map(tagUrlErrors, error => (
              <StatusLabel type={error} i18n={i18n} success={false} />
            ))
          }
        </Stack>
      );
    }
    return '';
  }, [tagUrlErrors, i18n]);

  return (
    <div className="third-party-measurement-tile">
      {(
        !connected ?
        (
          <Stack className="disconnected-status-tile" horizontal={false} tokens={{ childrenGap: 8 }} style={styles.disconnectedStatusTile.root}>
            <Dropdown
              className="vendor-name-dropdown"
              defaultSelectedKey={options[0].key}
              options={options}
              onRenderLabel={onRenderLabelDropDown}
              disabled
            />
            <TextField
              className="tag-url-input"
              multiline
              autoAdjustHeight
              onRenderLabel={onRenderLabelTextField}
              onChange={onTagUrlChange}
              errorMessage={tagErrorMessage}
            />
            <Button className="save-button" text={i18n.getString(_TL_('Add'))} onClick={save} style={styles.saveButton.root} />
          </Stack>
        ) : (
          <Stack className="connected-status-tile" horizontal={false} tokens={{ childrenGap: 4 }} style={styles.connectedStatusTile.root}>
            <Label
              className="connected-status-label"
              style={styles.connectedStatusLabel.root}
            >
              {i18n.getString(_TL_('Added'))}
            </Label>
            <Stack className="verified-tracking-data-stack" horizontal wrap tokens={{ childrenGap: 5 }}>
              <StatusLabel type={advertiserEntityIdKey} value={verifiedTrackingData[advertiserEntityIdKey]} i18n={i18n} success />
              <StatusLabel type={publisherEntityIdKey} value={verifiedTrackingData[publisherEntityIdKey]} i18n={i18n} success />
            </Stack>
            <Button className="remove-button" text={i18n.getString(_TL_('Remove'))} onClick={remove} style={styles.removeButton.root} />
          </Stack>
        )
      )}
    </div>
  );
};

ThirdPartyMeasurement.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      VerifiedTrackingSettings: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  adType: PropTypes.string,
  overrideStyles: PropTypes.shape({}),
};

ThirdPartyMeasurement.defaultProps = {
  adType: 'video',
  overrideStyles: {},
};

StatusLabel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  success: PropTypes.bool.isRequired,
  value: PropTypes.string,
};

StatusLabel.defaultProps = {
  value: '',
};

export const ThirdPartyMeasurementWithValidation = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(ThirdPartyMeasurement))));
